import BigNumber from 'bignumber.js'

// Participant contract from the merged QR API. Kept outside generated code because
// the published result schema still omits REWARD_PENDING_KYC.
export type QrCampaign = {
  id: string
  tenant_id: string
  type: string
  name?: string
  start_at: string
  end_at: string
  initial_user_balance: string
}
export type QrResult = {
  user_id?: string
  status?: string
  deactivation_status?: string
}
export type QrParticipation = { challenge: QrCampaign; result: QrResult }
export type QrReward = {
  trading_challenge_id: string
  state: 'NOT_ELIGIBLE' | 'ELIGIBLE' | 'PENDING_KYC' | 'PAID' | 'LAPSED'
  promo_credit: string
  min_reward_amount: string
  max_reward_amount: string
  reward_deadline_at: string
  kyc_completed: boolean
  blockers: string[]
  current_equity?: string
  eligible_profit?: string
  projected_reward?: string
  reward_amount?: string
  kyc_url?: string
}
export type QrRewardAccepted = {
  trading_challenge_id: string
  user_id: string
  status: 'REWARD_PENDING_KYC'
  reward_amount: string
  reward_deadline_at: string
  kyc_url?: string
}
export type QrRequest = <T>(
  path: string,
  options?: { method?: 'GET' | 'POST'; signal?: AbortSignal },
) => Promise<T>

export async function loadParticipations(
  tenant: string,
  userId: string,
  request: QrRequest,
  signal?: AbortSignal,
): Promise<QrParticipation[]> {
  // These two public read routes return complete arrays (no cursor/limit contract).
  const campaigns = await request<QrCampaign[]>(
    `/v1/trading_challenges?tenant_id=${encodeURIComponent(tenant)}&type=QR_PROMO`,
    { signal },
  )
  return (
    await Promise.all(
      campaigns
        .filter((c) => c.type === 'QR_PROMO' && c.tenant_id === tenant)
        .map(async (challenge) => {
          const results = await request<QrResult[]>(
            `/v1/trading_challenges/${encodeURIComponent(challenge.id)}/results`,
            { signal },
          )
          const result = results.find((r) => r.user_id === userId)
          return result ? { challenge, result } : null
        }),
    )
  ).filter((p): p is QrParticipation => p !== null)
}

export function formatUsd(value?: string) {
  if (value === undefined || value === null || value === '') return '—'
  const amount = new BigNumber(value)
  if (!amount.isFinite()) return '—'
  return `$${amount.toFormat(Math.max(2, amount.decimalPlaces() ?? 0))}`
}
// The minimum reward is profit above the promotional credit, so the equity target includes both.
export function targetEquity(reward: QrReward) {
  return new BigNumber(reward.promo_credit).plus(reward.min_reward_amount).toFixed()
}
export function safeKycUrl(value?: string) {
  if (!value) return undefined
  try {
    const url = new URL(value)
    return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined
  } catch {
    return undefined
  }
}
export function isQrTradingLocked(
  result: QrResult,
  reward: QrReward | undefined,
  pending: boolean,
) {
  return (
    pending ||
    result.status === 'PENDING' ||
    result.status === 'REWARD_PENDING_KYC' ||
    result.deactivation_status === 'DEACTIVATED' ||
    reward?.state === 'PENDING_KYC'
  )
}

type RewardView = {
  title: string
  text: string
  amount?: string
  action?: 'claim' | 'kyc' | 'withdraw'
}
export function rewardView(reward: QrReward): RewardView {
  switch (reward.state) {
    case 'ELIGIBLE':
      return {
        title: 'Your reward is ready',
        text: 'Keep trading or request your reward now. Eligibility and the projection can change with your equity.',
        amount: reward.projected_reward,
        action:
          reward.projected_reward !== undefined && reward.blockers.length === 0
            ? 'claim'
            : undefined,
      }
    case 'PENDING_KYC':
      return {
        title: reward.kyc_completed ? 'Verification received' : 'Reward request submitted',
        text: reward.kyc_completed
          ? 'Settlement is processing. Trading remains locked until the server completes settlement.'
          : reward.blockers.includes('KYC_URL_UNAVAILABLE') || !safeKycUrl(reward.kyc_url)
            ? 'Trading is locked. Verification is currently unavailable. Refresh the status or contact support before the deadline.'
            : 'Trading is locked. Complete verification by the deadline to receive your reward.',
        amount: reward.reward_amount,
        action:
          !reward.kyc_completed &&
          !reward.blockers.includes('KYC_URL_UNAVAILABLE') &&
          safeKycUrl(reward.kyc_url)
            ? 'kyc'
            : undefined,
      }
    case 'PAID':
      return {
        title: 'Reward credited',
        text: 'Your reward has been credited as ordinary USD. Use your existing withdrawal flow when you are ready.',
        amount: reward.reward_amount,
        action: 'withdraw',
      }
    case 'LAPSED':
      return {
        title: 'Reward deadline missed',
        text: 'This reward has lapsed. No payout is available. Account cleanup is handled by the server.',
        amount: reward.reward_amount,
      }
    default:
      if (reward.blockers.includes('BUSTED'))
        return {
          title: 'Challenge ended for your account',
          text: 'No promotional reward is available. Promotional credit cannot be refilled.',
        }
      if (reward.blockers.includes('REWARD_DEADLINE_PASSED'))
        return {
          title: 'Reward request window closed',
          text: 'The deadline for requesting this reward has passed.',
        }
      if (reward.blockers.includes('OTHER_CHALLENGE_FUNDED'))
        return {
          title: 'Reward unavailable',
          text: 'Another funded challenge may contribute to your equity. Contact support to check your participation.',
        }
      if (reward.blockers.includes('CHALLENGE_NOT_STARTED'))
        return {
          title: 'Challenge not started',
          text: 'Your promotional challenge is being prepared or has not started yet.',
        }
      return {
        title: 'Promotional challenge',
        text: 'Reach the target equity to unlock a reward. The initial promotional credit is excluded from your reward.',
      }
  }
}

// Each new funding/reward phase gets its own finite budget. A failed read
// consumes an attempt as well; polling never retries mutations.
export function createQrPollBudget() {
  let attempts = 0
  return {
    nextDelay: (): number | false =>
      attempts >= 12 ? false : Math.min(30_000, 5000 * 2 ** attempts++),
  }
}
